import type { StreamingSettings } from '../core/model';
import type { BroadcastStartOptions, LinkStatus } from '../net/api';

/**
 * Options for the studio's signalling room (`net/broadcast-host.ts`). In p2p mode the room carries
 * viewers and guests. In swarm mode viewers watch through the swarm, so the same room is opened for
 * guests only: no viewer slots, everything else (links, lobby, bans) unchanged. The room is built
 * from the signalling settings alone; the swarm stream key never reaches it or its links.
 */
export function hostRoomOptions(settings: StreamingSettings, stream: MediaStream): BroadcastStartOptions {
  return {
    videoKbps: settings.videoKbps,
    audioKbps: settings.audioKbps,
    codec: settings.codec,
    degradation: settings.degradation,
    signalingUrl: settings.signalingUrl,
    stream,
    iceServers: settings.iceServers,
    maxViewers: settings.mode === 'p2p' ? settings.maxViewers : 0,
    allowGuests: settings.allowGuests,
    guestLink: settings.guestLink,
    guestApproval: settings.guestApproval,
    iceTransportPolicy: settings.iceTransportPolicy,
  };
}

/**
 * Whether swarm mode should open the guest room now: the swarm is on air, guests are accepted and
 * the room is not already open. A room that failed ('error') may be opened again.
 */
export function swarmGuestRoomWanted(
  settings: Pick<StreamingSettings, 'mode' | 'allowGuests'>,
  status: { swarm: LinkStatus; broadcast: LinkStatus },
): boolean {
  return (
    settings.mode === 'swarm' &&
    settings.allowGuests &&
    (status.swarm === 'live' || status.swarm === 'reconnecting') &&
    (status.broadcast === 'idle' || status.broadcast === 'error')
  );
}
